import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useParams } from 'react-router'
import classNames from 'classnames'
import { JsonView } from 'shared/ui/JsonView/JsonView'

import {
  Breadcrumbs,
  Button,
  ButtonCopy,
  Checkbox,
  Divider,
  EnumSpinnerLoaderPosition,
  FullPageWrapper,
  Icon,
  SpinnerLoader,
  Switch,
  TextFieldWithLabel,
  Tooltip,
  Typography,
} from 'shared/ui'
import { LINK_WEBHOOK_GUIDE } from 'shared/constants/links'
import { TextFieldWithFixedText } from 'shared/ui/TextFieldWithFixedText/TextFieldWithFixedText'
import { Collapse } from 'shared/ui/Collapse'
import { IntegrationsRoutes, SettingsRoutes } from 'entities/ExportRouters'
import { webhookEventMappings } from 'pages/settings/pages/integrations/pages/Webhooks/constants/webhookEventMappings'
import { Chip } from 'pages/settings/pages/integrations/pages/Webhooks/ui/Chip/Chip'
import { WebhookPageStore } from 'pages/settings/pages/integrations/pages/Webhooks/store/WebhookPageStore'
import { useWebhookIntegrationContext } from 'pages/settings/pages/integrations/layout/WebhookLayout'
import styles from './styles.module.scss'

export const WebhookPage = observer(() => {
  const { id } = useParams()
  const { webhooksStore } = useWebhookIntegrationContext()

  const webhookPageStore = useMemo(() => new WebhookPageStore(webhooksStore, id), [id])

  const { events, isLoading, eventsError, switchEvent, switchAllEvent } = webhookPageStore

  const submitButtonText = id ? 'Save webhook' : 'Create webhook'

  return (
    <FullPageWrapper className={styles.container}>
      {isLoading && (
        <SpinnerLoader
          ariaLabel={'WebhookPage'}
          position={EnumSpinnerLoaderPosition.Absolute}
          wrapperClassName={styles.spinnerLoader}
        />
      )}
      <div className={styles.content}>
        <Breadcrumbs
          customPath={`/${SettingsRoutes.root}/${IntegrationsRoutes.root}/${IntegrationsRoutes.webhooks}`}
        />

        <header className={styles.paddingContainer}>
          <div className={styles.title}>
            <Typography variant='heading-lg' ariaLabel='WebhookPage_title'>
              Webhook setup
            </Typography>
            <Typography
              variant='body-md-regular'
              color='var(--content-primary-tertiary)'
              ariaLabel='WebhookPage_description'
            >
              Edit your webhook details
            </Typography>
          </div>

          <div className={styles.actions}>
            <Button
              tag='a'
              text='Docs'
              size='medium'
              contained='secondary'
              iconProps={{ icon: 'link' }}
              href={LINK_WEBHOOK_GUIDE}
              target='_blank'
              rel='noreferrer'
              className={styles.docsButton}
            />
            <Button
              text={submitButtonText}
              typeBtn='outlined'
              size='medium'
              onClick={webhookPageStore.submitWebhook}
              loading={webhookPageStore.isSaving}
            />
          </div>
        </header>

        <div className={styles.contentSection}>
          <Typography
            tag='div'
            marginTop={-2}
            marginBottom={34}
            variant='body-lg-medium'
            className={styles.paddingContainer}
            ariaLabel='WebhookPage_generalSettings'
          >
            General settings
          </Typography>

          <div className={classNames(styles.contentLine, styles.paddingContainer)}>
            <div className={styles.contentLineDesc}>
              <Typography variant='body-md-medium' ariaLabel='WebhookPage_description'>
                Webhook Name
              </Typography>
              <Typography
                variant='body-md-regular'
                ariaLabel='WebhookPage_description'
                color='var(--content-primary-tertiary)'
              >
                Give your webhook a name
              </Typography>
            </div>

            <TextFieldWithLabel
              textFieldProps={{
                value: webhookPageStore.name,
                onChange: (value) => webhookPageStore.setName(value),
                onBlur: () => webhookPageStore.validateName(),
                InputProps: {
                  placeholder: 'Enter name',
                },
                error: webhookPageStore.nameError,
                className: styles.contentLineInput,
              }}
              widthWrap={354}
              errorMessage={webhookPageStore.nameError}
              limit={255}
            />
          </div>

          <div className={classNames(styles.contentLine, styles.paddingContainer)}>
            <div className={styles.contentLineDesc}>
              <Typography variant='body-md-medium' ariaLabel='WebhookPage_description'>
                URL
              </Typography>
              <Typography
                variant='body-md-regular'
                ariaLabel='WebhookPage_description'
                color='var(--content-primary-tertiary)'
              >
                Enter the destination URL for your webhook
              </Typography>
            </div>

            <TextFieldWithLabel
              textFieldProps={{
                value: webhookPageStore.url,
                onChange: (value) => webhookPageStore.setUrl(value),
                onBlur: () => webhookPageStore.validateUrl(),
                InputProps: {
                  placeholder: 'Enter url',
                },
                error: webhookPageStore.urlError,
                className: styles.contentLineInput,
              }}
              widthWrap={354}
              errorMessage={webhookPageStore.urlError}
            />
          </div>

          <div className={classNames(styles.contentLine, styles.paddingContainer)}>
            <div className={styles.contentLineDesc}>
              <div className={styles.contentLineDescTitle}>
                <Typography variant='body-md-medium' ariaLabel='WebhookPage_description'>
                  Event Throttling
                </Typography>
                <Tooltip
                  type='description'
                  width='200px'
                  placement='top'
                  label='Maximum number of requests Salesmsg will try sending.'
                  desc='Request can contain up to 100 events.'
                >
                  <Icon icon='question' fontSize={14} color='var(--content-neutral-primary)' />
                </Tooltip>
              </div>
              <Typography
                variant='body-md-regular'
                ariaLabel='WebhookPage_description'
                color='var(--content-primary-tertiary)'
              >
                Limit events sent per second.
              </Typography>
            </div>

            <div className={styles.perSecondWrap}>
              <TextFieldWithFixedText
                fixedValue='per second'
                value={webhookPageStore.throttleLimit}
                defaultValue='1'
                setValue={webhookPageStore.setThrottleLimit}
                blurCallback={(value) => {
                  webhookPageStore.setThrottleLimit(value)
                }}
              />
            </div>
          </div>

          <Divider
            sx={{
              marginBottom: '28px',
              marginTop: '30px',
              marginLeft: '20px',
              marginRight: '20px',
              color: 'var(--border-neutral-default)',
            }}
          />

          <Typography
            tag='div'
            variant='body-lg-medium'
            ariaLabel='WebhookPage_description'
            marginBottom={24}
            className={classNames(styles.paddingContainer, styles.eventsError)}
          >
            Select webhooks
            {eventsError && (
              <Tooltip label='At least one event must be selected' placement='top' color='red'>
                <Icon
                  icon='alertTriangle'
                  fontSize={16}
                  color='var(--action-contained-destructive-primary-normal)'
                />
              </Tooltip>
            )}
          </Typography>

          {webhookEventMappings.map((item) => {
            const selectedEventsInGroup = events.filter((event) => event.includes(item.value))
            const totalEventsInGroup = item.events.length
            const isIndeterminate =
              selectedEventsInGroup.length > 0 && selectedEventsInGroup.length < totalEventsInGroup

            return (
              <Collapse
                key={item.value}
                variant='transparent'
                title={item.title}
                isOpen={false}
                className={styles.collapse}
                headerClassName={styles.collapseHeader}
                leftContent={(isOpen) => (
                  <div onClick={(e) => isOpen && e.stopPropagation()}>
                    <Checkbox
                      isIndeterminate={isIndeterminate}
                      checked={Boolean(selectedEventsInGroup.length)}
                      onChange={(value) =>
                        switchAllEvent(
                          item.events.map((event) => `${item.value}${event.value}`),
                          value
                        )
                      }
                    />
                  </div>
                )}
              >
                {item.events.map((event) => (
                  <div key={event.title} className={styles.eventLine}>
                    <div className={styles.eventLineDesc}>
                      <Typography variant='body-md-medium' ariaLabel='WebhookPage_description'>
                        {event.title}
                      </Typography>
                      <Chip line={`${item.value}${event.value}`} />
                    </div>

                    <Switch
                      size='middle'
                      value={events.includes(`${item.value}${event.value}`)}
                      onChange={() => switchEvent(`${item.value}${event.value}`)}
                    />
                  </div>
                ))}
                <Divider
                  sx={{
                    marginTop: '8px',
                    marginLeft: '20px',
                    marginRight: '20px',
                    color: 'var(--border-neutral-default)',
                  }}
                />
              </Collapse>
            )
          })}

          {webhookPageStore.parsedEventsPreview && (
            <>
              <div className={styles.payloadHeader}>
                <Typography variant='body-lg-medium' ariaLabel='WebhookPage_description'>
                  Payload info
                </Typography>

                <ButtonCopy value={JSON.stringify(webhookPageStore.parsedEventsPreview)} />
              </div>
              <JsonView
                data={webhookPageStore.parsedEventsPreview}
                shouldExpandNode={(path) => path < 2}
                compactTopLevel={true}
                style={{
                  container: styles.jsonViewContainer,
                  childFieldsContainer: styles.jsonViewChildFieldsContainer,
                  label: styles.jsonViewLabel,
                  punctuation: styles.jsonViewPunctuation,
                  nullValue: styles.jsonViewNullValue,
                  undefinedValue: styles.jsonViewUndefinedValue,
                  stringValue: styles.jsonViewStringValue,
                  booleanValue: styles.jsonViewBooleanValue,
                  numberValue: styles.jsonViewNumberValue,
                  otherValue: styles.jsonViewOtherValue,
                }}
              />
            </>
          )}
        </div>
      </div>
    </FullPageWrapper>
  )
})
